/**
 * Operator-admin login. Hits /v1/auth/login like the dashboard,
 * persists the access token in localStorage via `authStore`, and
 * surfaces 2FA when the account has it enrolled. Backend's dual-auth
 * dep (require_admin_session_or_operator_auth) then accepts that
 * token for every /v1/admin/* call.
 *
 * Trust-mutating writes (cert publish, clear-keypair) still require
 * Ed25519 via `dovach-op` and are NOT reachable from this UI.
 */

import {
  Alert,
  Button,
  Container,
  Paper,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";

import { ApiError, api, authStore } from "../lib/api";

interface Props {
  onLoggedIn: () => void;
}

type Step =
  | { kind: "credentials" }
  | { kind: "needs_2fa"; partialToken: string };

export function LoginScreen({ onLoggedIn }: Props) {
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await api.login(email, password);
      if (resp.needs_2fa && resp.partial_token) {
        setStep({ kind: "needs_2fa", partialToken: resp.partial_token });
      } else if (resp.tokens) {
        authStore.set(resp.tokens.access_token);
        // Optional: confirm is_operator before letting the panels render.
        try {
          await api.me();
          onLoggedIn();
        } catch (e) {
          authStore.clear();
          throw e;
        }
      }
    } catch (err) {
      setError(err instanceof ApiError ? friendly(err.detail) : "שגיאה לא צפויה");
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step.kind !== "needs_2fa") return;
    setBusy(true);
    setError(null);
    try {
      const tokens = await api.verify2fa(step.partialToken, code);
      authStore.set(tokens.access_token);
      onLoggedIn();
    } catch (err) {
      setError(err instanceof ApiError ? friendly(err.detail) : "שגיאה לא צפויה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" pt={64}>
      <Paper p="xl" withBorder>
        <Stack>
          <Title order={2}>Operator Admin</Title>
          {step.kind === "credentials" ? (
            <form onSubmit={submitCredentials}>
              <Stack>
                <Text c="dimmed" size="sm">
                  Sign in with your operator account (is_operator=true).
                </Text>
                <TextInput
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  autoFocus
                />
                <PasswordInput
                  label="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                />
                {error && <Alert color="red">{error}</Alert>}
                <Button type="submit" loading={busy} disabled={!email || !password}>
                  Sign in
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={submit2fa}>
              <Stack>
                <Text c="dimmed" size="sm">
                  Enter the 6-digit code from your authenticator.
                </Text>
                <PinInput length={6} value={code} onChange={setCode} type="number" oneTimeCode />
                {error && <Alert color="red">{error}</Alert>}
                <Button type="submit" loading={busy} disabled={code.length !== 6}>
                  Verify
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

function friendly(detail: string): string {
  switch (detail) {
    case "invalid_credentials":
      return "Wrong email or password.";
    case "login_rate_limited":
      return "Too many attempts. Wait a minute and try again.";
    case "not_an_operator":
      return "This account isn't an operator — ask for is_operator=true.";
    case "operator_auth_missing":
      return "Session not accepted by the API. Sign in again.";
    default:
      return detail;
  }
}
